<template>
  <v-container class="mb-8">
    <ViewHeader
      class="qa-header"
      :title="$t('message.questionsAndAnswers.title')"
    >
      <div class="d-flex align-center">
        <DatePicker v-if="date" :value="date" @input="handleDateChange" />

        <ui-button
          color="primary"
          class="ml-6"
          icon="icon-filter"
          outlined
          @click="toggleFilterDialog"
        >
          {{ $t("message.reviews.filter") }}
        </ui-button>
        <ui-button
          v-if="canAddAndEditQa"
          class="ml-4"
          color="primary"
          icon="mdi-message-outline"
          @click="toggleManageDialog"
        >
          {{ $t("message.questionsAndAnswers.askQuestion") }}
        </ui-button>
      </div>
    </ViewHeader>
    <v-card class="card-rounded qa-card">
      <v-tabs v-model="selectedTab" slider-color="primary" class="tabs">
        <v-tab
          v-for="tab in tabs"
          :key="tab.id"
          :ripple="false"
          class="tabs__item"
        >
          {{ tab.text }}
        </v-tab>
      </v-tabs>
      <v-tabs-items
        v-model="selectedTab"
        class="tabs__content"
        :class="{ 'is-loading': isLoading }"
      >
        <v-tab-item v-for="tab in tabs" :key="tab.id" :transition="false">
          <no-data
            v-if="listItems.length === 0"
            :description="$t('message.questionsAndAnswers.noDataDescription')"
          />
          <div v-else class="list-container">
            <Pagination
              class="mb-4"
              :page="payload.page"
              :per-page="payload.page_size"
              :options="perPageOptions"
              :total-count="totalCount"
              :disabled="isLoading"
              @change="handlePageChange"
            />
            <ListItem
              v-for="item in listItems"
              :key="item.id"
              :data="item"
              class="list-item"
            />
            <Pagination
              class="mt-4"
              :show-options="false"
              :page="payload.page"
              :per-page="payload.page_size"
              :options="perPageOptions"
              :total-count="totalCount"
              :disabled="isLoading"
              @change="handlePageChange"
            />
          </div>
        </v-tab-item>
        <SectionLoader v-if="isLoading" status :opacity="1" class="loader" />
      </v-tabs-items>
    </v-card>
    <ManageDialog
      v-if="showManageDialog"
      @close="showManageDialog = false"
      @submit="handleQuestionSubmit"
    />
    <FilterDialogHoc
      v-if="showFilterDialog"
      :close-function="toggleFilterDialog"
      :filters="preselectedFilters"
      :show-comments-options="false"
      :show-ratings="false"
      @apply-filters="handleFilterApply"
    />
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader";
import NoData from "@/components/ui/NoData.vue";
import ListItem from "@/components/QuestionsAndAnswers/QaListItem.vue";
import ManageDialog from "@/components/QuestionsAndAnswers/QaManageDialog.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import DatePicker from "@/components/ReviewBooster/DatePicker.vue";
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { PERIOD } from "@/enums/period.enum";
import { mapState, mapActions } from "vuex";
import pickBy from "lodash/pickBy";
import { Permission } from "@/enums/permission";
import { permissionCheck } from "@/helpers/permission-check";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import Pagination from "@/components/List/Pagination";

const FilterDialogHoc = async () => {
  const filterDialogHoc = await import("@/components/hoc/FilterDialogHoc").then(
    m => m.default
  );
  return filterDialogHoc(() => import("@/components/Filter/dialog"));
};

const TAB_FILTER_ENUM = {
  all: "all",
  answered: "answered",
  unanswered: "unanswered"
};

const LOCATION_FILTER_ENUM = {
  facilities: "facilities",
  city: "city",
  groups: "facilitiesGroups"
};

export default {
  name: "QuestionAndAnswers",
  components: {
    ViewHeader,
    NoData,
    ListItem,
    ManageDialog,
    SectionLoader,
    DatePicker,
    Pagination,
    FilterDialogHoc: () => FilterDialogHoc()
  },
  data() {
    return {
      selectedTab: null,
      isLoading: true,
      date: null,
      tabs: [
        {
          id: 1,
          value: "all",
          text: this.$t("message.questionsAndAnswers.tabs.all")
        },
        {
          id: 2,
          value: "answered",
          text: this.$t("message.questionsAndAnswers.tabs.withAnswer")
        },
        {
          id: 3,
          value: "unanswered",
          text: this.$t("message.questionsAndAnswers.tabs.withoutAnswer")
        }
      ],
      showManageDialog: false,
      showFilterDialog: false,
      payload: { page: 1, page_size: 10 },
      perPageOptions: [10, 25, 50, 100]
    };
  },
  computed: {
    canAddAndEditQa() {
      return permissionCheck(Permission.ADD_AND_EDIT_Q_AND_A);
    },
    ...mapState({
      listItems: state => state.questionsAndAnswers.listItems,
      totalCount: state => state.questionsAndAnswers.totalCount
    }),
    preselectedFilters() {
      const filters = {
        ...this.payload,
        ...(this.payload.facility_ids && {
          facilitiesIds: this.payload.facility_ids
        })
      };
      return pickBy(filters, value => value !== null);
    },
    defaultStartDate() {
      return [
        moment()
          .subtract(1, PERIOD.MONTH)
          .add(1, PERIOD.DAY)
          .format(FORMAT.DATE),
        moment().format(FORMAT.DATE)
      ];
    }
  },
  created() {
    this.setDefaultDate();
  },
  methods: {
    handlePageChange({ page, perPage }) {
      this.payload = { ...this.payload, page, page_size: perPage };
    },
    toggleManageDialog() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.QA_ADD_QUESTION_AND_ANSWER
      });
      this.showManageDialog = true;
    },
    setDefaultDate() {
      this.date = this.defaultStartDate;

      this.setDatePayload(this.date);
    },
    setDatePayload(date) {
      const [dateFrom, dateTo] = date;
      if (dateFrom && dateTo) {
        const newPayload = { ...this.payload };
        newPayload.date_from = dateFrom;
        newPayload.date_to = dateTo;

        this.payload = {
          ...newPayload,
          ...this.getDefaultPagingPayload()
        };
      }
    },
    handleFilterApply(filters) {
      const filterMap = {
        [LOCATION_FILTER_ENUM.facilities]: {
          key: "facility_ids",
          value: filters => filters.facilitiesIds.join(",")
        },
        [LOCATION_FILTER_ENUM.city]: {
          key: "cities",
          value: filters => filters.cities.join(",")
        },
        [LOCATION_FILTER_ENUM.groups]: {
          key: "groups",
          value: filters => filters.groups.join(",")
        }
      };

      const newPayload = this.payload;

      Object.values(filterMap).forEach(({ key }) => {
        delete newPayload[key];
      });

      const selectedFilter = filterMap[filters.searchType];
      if (selectedFilter) {
        newPayload[selectedFilter.key] = selectedFilter.value(filters);
      }

      this.payload = {
        ...newPayload,
        ...this.getDefaultPagingPayload(),
        searchType: filters.searchType
      };
    },
    toggleFilterDialog() {
      this.showFilterDialog = !this.showFilterDialog;
    },
    ...mapActions({
      getQuestions: "questionsAndAnswers/getQuestions",
      createQuestions: "questionsAndAnswers/createQuestions"
    }),
    async handleQuestionSubmit(payload) {
      this.isLoading = true;
      this.showManageDialog = false;
      await this.createQuestions(payload);
      this.payload = { ...this.payload, ...this.getDefaultPagingPayload() };
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.QA_ADDED_QUESTION_AND_ANSWER
      });
      this.selectedTab = 0;
      this.isLoading = false;
    },
    getDefaultPagingPayload() {
      return { page: 1, page_size: 10 };
    },
    async handleDateChange(date) {
      this.date = date;
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_DATE_PERIOD,
        date_from: date[0],
        date_to: date[1]
      });
      this.setDatePayload(date);
    }
  },
  watch: {
    selectedTab(currentTab) {
      const selectedFilter = this.tabs.find(tab => tab.id === currentTab + 1);

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.QA_FILTERED_BY_REPLY,
        filter: selectedFilter.value
      });

      if (selectedFilter) {
        const newPayload = { ...this.payload };
        if (selectedFilter.value === TAB_FILTER_ENUM.answered) {
          newPayload.has_answers = true;
        } else if (selectedFilter.value === TAB_FILTER_ENUM.unanswered) {
          newPayload.has_answers = false;
        } else {
          delete newPayload.has_answers;
        }
        this.payload = { ...newPayload, ...this.getDefaultPagingPayload() };
      }
    },
    payload: {
      async handler(current) {
        this.isLoading = true;
        await this.getQuestions(current);
        this.isLoading = false;
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
$last-question-selector: "> div:last-child .question__body:before";

@mixin hide-last-question {
  .question__items {
    #{$last-question-selector} {
      display: none;
    }
  }
}

.qa-header {
  padding-left: 0;
  padding-right: 0;
  margin-bottom: $spacing * 2;
}
.qa-card {
  min-height: 589px;
  position: relative;
  padding: 0 $spacing * 4 0 $spacing * 4;
}

.tabs {
  border-bottom: 1px solid $gray-200;

  &__item {
    text-transform: unset;
    letter-spacing: unset;
    padding: $spacing $spacing * 6;
  }

  &__content {
    padding: $spacing * 4 0;
    position: relative;

    &.is-loading {
      overflow: hidden;
      max-height: 500px;
    }
  }
}

.list-item {
  &::v-deep .nested-container {
    &:not(:has(.toggle-button)) {
      @include hide-last-question;
    }
  }

  &::v-deep .ps-container {
    @include hide-last-question;
  }
}

.list-container {
  display: flex;
  flex-direction: column;
}

.loader {
  max-height: 500px;
}
</style>
